import { memo, useMemo } from 'react';
import {
  Grid,
  WidgetContainer,
  DateRangePickerWithTextField,
  Typography,
  G500,
  G900,
  useTranslations,
  formatDate,
  DateTimeFormatEnum,
  IDateRangePickerWithTextFieldProps,
  DateRange,
  TypographyWithHelp,
} from '@uniqkey-frontend/shared-app';
import { IChartDataPoint } from '../../../../../../helpers/ChartJSLib/interfaces';
import TimeToPercentLineChart from '../../../../../../components/charts/TimeToPercentLineChart';

interface IDashboardAverageSecurityScoreWithGraphWidgetContainerProps extends DateRange, Pick<
  IDateRangePickerWithTextFieldProps, 'onSubmit' | 'onClear'
> {
  headerTranslationKey: string;
  tooltipTranslationKey: string;
  updatedAtTranslationKey: string;
  noDataTranslationKey: string;
  updatedAt: string;
  isLoading: boolean;
  data: IChartDataPoint[];
}

const CONTAINER_SX = { height: '100%', minHeight: 270 };

const DashboardAverageSecurityScoreWithGraphWidgetContainer = (
  props: IDashboardAverageSecurityScoreWithGraphWidgetContainerProps,
) => {
  const { t, currentLanguage } = useTranslations();
  const {
    headerTranslationKey,
    tooltipTranslationKey,
    updatedAtTranslationKey,
    noDataTranslationKey,
    updatedAt: preUpdatedAt,
    from,
    to,
    onSubmit,
    onClear,
    isLoading,
    data,
  } = props;

  const updatedAt = useMemo(
    () => formatDate({
      date: preUpdatedAt,
      locale: currentLanguage,
    }, DateTimeFormatEnum.FULL),
    [currentLanguage, preUpdatedAt],
  );

  return (
    <WidgetContainer withShadow isLoading={isLoading} sx={CONTAINER_SX}>
      <Grid container flexDirection="column" sx={CONTAINER_SX}>
        <Grid container justifyContent="space-between" rowGap={1}>
          <Grid item flexDirection="column">
            <Grid item>
              <TypographyWithHelp
                color={G900}
                helperText={t(tooltipTranslationKey)}
              >
                {t(headerTranslationKey)}
              </TypographyWithHelp>
            </Grid>
            <Grid item mt={-0.5}>
              {updatedAt && (
                <Typography variant="caption" color={G500}>
                  {t(updatedAtTranslationKey, { updatedAt })}
                </Typography>
              )}
            </Grid>
          </Grid>
          <DateRangePickerWithTextField
            from={from}
            to={to}
            onSubmit={onSubmit}
            onClear={onClear}
            t={t}
          />
        </Grid>
        <Grid container flex={1}>
          <TimeToPercentLineChart
            data={data}
            noDataTranslationKey={noDataTranslationKey}
          />
        </Grid>
      </Grid>
    </WidgetContainer>
  );
};

export default memo(DashboardAverageSecurityScoreWithGraphWidgetContainer);
